import React, {useEffect, useRef} from 'react';
import {View, Animated, Easing, Platform} from 'react-native';
import {Svg} from 'react-native-svg';
import OutlinedSvgText from '../OutlinedSvgText';
import Sizes from '../../../Utilities/Sizes/Sizes';
import SkillzStyle from '../../../SkillzStyle/SkillzStyle';
import {StyleSheet} from 'react-native';
import SkillzImage from 'SkillzImage';

const {fontBold, fontBoldItalic} = SkillzStyle.fullStyles;

interface AnimatedDiscountBadgeProps {
  style?: any;
  showAnimation?: boolean;
  discountPercentage: number | undefined;
  hasAnimated?: boolean;
}

const AnimatedDiscountBadge: React.FC<AnimatedDiscountBadgeProps> = ({
  style,
  showAnimation,
  discountPercentage,
  hasAnimated,
}) => {
  const enteringAnimation = useRef(new Animated.Value(-1)).current;
  const shiverAnimation = useRef(new Animated.Value(1)).current;

  // Dynamic font size that scales with device size
  const dynamicFontSize = Sizes.dynamic({
    sm: 11,
    lg: 12,
    xxlg: 14,
  });

  useEffect(() => {
    if (hasAnimated) {
      enteringAnimation.setValue(1.06);
      shiverAnimation.setValue(1);
      return;
    }
    if (showAnimation) {
      Animated.timing(enteringAnimation, {
        toValue: 1.06,
        duration: 2000,
        easing: Easing.elastic(1),
        useNativeDriver: true,
      }).start((finished) => {
        if (finished) {
          Animated.loop(
            Animated.sequence([
              Animated.timing(shiverAnimation, {
                toValue: -1,
                duration: 1000,
                useNativeDriver: true,
              }),
              Animated.timing(shiverAnimation, {
                toValue: 1,
                duration: 1000,
                useNativeDriver: true,
              }),
            ]),
            {iterations: 2}
          ).start();
        }
      });
    }
    return () => {
      enteringAnimation.stopAnimation();
      shiverAnimation.stopAnimation();
    };
  }, [showAnimation, hasAnimated]);

  const animatedStyle = {
    /**
     Rotate image from top right by 45 degree and end with spring animation 
    Increase opacity of image from 0 to 1
    */
    transform: [
      {
        rotateZ: enteringAnimation.interpolate({
          inputRange: [-1, 1.06],
          outputRange: ['180deg', '0deg'],
        }),
      },
      {
        rotateZ: shiverAnimation.interpolate({
          inputRange: [-1, 1],
          outputRange: ['-5deg', '5deg'],
        }),
      },
    ],
    opacity: enteringAnimation,
  };

  return (
    <Animated.View style={[style, {transformOrigin: 'top right'}, animatedStyle]}>
      <View style={styles.container}>
        {/* Background Image */}
        <SkillzImage
          source={Platform.OS === 'web' ? 'discount_bg_image.svg' : 'discount_bg_image.png'}
          style={styles.svgBackground}
          resizeMode='contain'
        />
        {/* Text Overlay */}
        <Svg height='38' width='90' style={styles.zIndex}>
          <OutlinedSvgText
            x='40'
            y='16'
            fontSize={dynamicFontSize}
            fontWeight='700'
            fontFamily={fontBoldItalic.fontFamily}
            textAnchor='middle'
            stroke='#21631D'
            strokeWidth={2.1}
            fill='#fff'
          >
            {discountPercentage ? `${discountPercentage}%` : '0%'}
          </OutlinedSvgText>
          <OutlinedSvgText
            x='40'
            y='28'
            fontSize={dynamicFontSize}
            fontWeight='700'
            fontFamily={fontBold.fontFamily}
            textAnchor='middle'
            stroke='#21631D'
            strokeWidth={2.1}
            fill='#fff'
          >
            OFF
          </OutlinedSvgText>
        </Svg>
      </View>
    </Animated.View>
  );
};

export default React.memo(AnimatedDiscountBadge);

const styles = StyleSheet.create({
  container: {
    width: 63,
    height: 47,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  svgBackground: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  zIndex: {
    zIndex: 1,
  },
});
